
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import {
  RuleCondition, RuleConditionItem, RuleConditionBlock, RuleConditionDefinition,
} from '@/types';
import { isBlock } from '@/helpers/ruleHelpers';
import RuleCustomValueDisplay from './RuleCustomValueDisplay.vue';
import RuleItemPickerDialog from './RuleItemPickerDialog.vue';

@Component({
  name: 'RuleConditionList',
  components: { RuleCustomValueDisplay, RuleItemPickerDialog },
})
export default class RuleConditionList extends Vue {
  @Prop({ type: Array, required: true }) readonly value!: RuleConditionItem[];
  @Prop({ type: Array, required: true }) readonly conditionDefinitions!: RuleConditionDefinition[];
  @Prop({ type: Array, default: () => [] }) readonly users!: any[];
  @Prop({ type: Array, default: () => [] }) readonly groups!: any[];
  @Prop({ type: Array, default: () => [] }) readonly categories!: any[];
  @Prop({ type: Array, default: () => [] }) readonly tags!: any[];
  @Prop({ type: Array, default: () => [] }) readonly templates!: any[];
  @Prop({ type: Array, default: () => [] }) readonly checklists!: any[];
  @Prop({ type: Number, default: 0 }) readonly depth!: number;

  // Indent capping: max 6 livelli di indentazione visiva (oltre, costante)
  // per evitare scroll orizzontale su alberi profondi.
  get indentPx(): number {
    return Math.min(this.depth, 6) * 8;
  }

  dialogOpen = false;
  // Stati separati per i due flussi del dialog picker:
  //  - editingIndex: indice della condizione semplice in `value` da SOSTITUIRE
  //    (settato solo da openEditDialog; null altrimenti).
  //  - insertParentIndex: indice del blocco in `value` IN CUI inserire il
  //    nuovo figlio (settato da openAddDialog quando si aggiunge dentro un
  //    blocco; null per append top-level).
  editingIndex: number | null = null;
  insertParentIndex: number | null = null;
  editingItem: RuleCondition | null = null;

  blockLabels: Record<string, string> = {
    and: 'Se tutte le condizioni sono vere',
    or: 'Se almeno una condizione è vera',
    not: 'Se la condizione è falsa',
  };

  blockIcons: Record<string, string> = {
    and: 'mdi-gate-and',
    or: 'mdi-gate-or',
    not: 'mdi-gate-not',
  };

  isBlockItem(item: RuleConditionItem): item is RuleConditionBlock {
    return isBlock(item);
  }

  // Un blocco NOT accetta esattamente 1 figlio (cond o blocco).
  canAddChild(item: RuleConditionItem): boolean {
    if (!this.isBlockItem(item)) return false;
    if (item.block === 'not' && item.conditions.length >= 1) return false;
    return true;
  }

  openAddDialog(parentIndex: number | null): void {
    this.insertParentIndex = parentIndex;
    this.editingIndex = null;
    this.editingItem = null;
    this.dialogOpen = true;
  }

  openEditDialog(index: number): void {
    const it = this.value[index];
    if (this.isBlockItem(it)) return;
    this.insertParentIndex = null;
    this.editingIndex = index;
    this.editingItem = it as RuleCondition;
    this.dialogOpen = true;
  }

  removeItem(index: number): void {
    const it = this.value[index];
    const msg = this.isBlockItem(it)
      ? 'Eliminare questo blocco e tutte le sue condizioni?'
      : 'Eliminare questa condizione?';
    if (!window.confirm(msg)) return;
    const next = this.value.filter((_, i) => i !== index);
    this.$emit('input', next);
  }

  onItemAdded(item: RuleCondition): void {
    this.appendOrInsert(item);
    this.dialogOpen = false;
  }

  onBlockAdded(block: RuleConditionBlock): void {
    this.appendOrInsert(block);
    this.dialogOpen = false;
  }

  private appendOrInsert(item: RuleConditionItem): void {
    if (this.insertParentIndex === null) {
      // Append in fondo a questa lista
      this.$emit('input', [...this.value, item]);
      return;
    }
    // Aggiungi DENTRO il blocco target (item è figlio di value[insertParentIndex])
    const target = this.value[this.insertParentIndex];
    if (!this.isBlockItem(target)) return;
    const updatedBlock: RuleConditionBlock = {
      block: target.block,
      conditions: [...target.conditions, item],
    };
    const next = this.value.slice();
    next[this.insertParentIndex] = updatedBlock;
    this.$emit('input', next);
  }

  onItemUpdated(item: RuleCondition): void {
    if (this.editingIndex === null) return;
    const next = this.value.slice();
    next[this.editingIndex] = item;
    this.$emit('input', next);
    this.dialogOpen = false;
  }

  onChildInput(blockIndex: number, newChildren: RuleConditionItem[]): void {
    const target = this.value[blockIndex];
    if (!this.isBlockItem(target)) return;
    const updatedBlock: RuleConditionBlock = {
      block: target.block,
      conditions: newChildren,
    };
    const next = this.value.slice();
    next[blockIndex] = updatedBlock;
    this.$emit('input', next);
  }
}
